import api from 'api/api'
import queryClient from 'api/queryClient'
import { QUEST_ENTITY_QUERY_POLICY } from 'api/queryPolicies'
import { cacheQuest, getCachedAnswer } from 'state/cache'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { Quest } from 'types/quests'

const getQuestQueryKey = questId => ['quest', String(questId)]

function cachedQuestContainsAnswer(quest: Quest | null | undefined, answerId: string) {
    if (!quest) return false
    const targetId = String(answerId)
    if (String(quest.parent?.id ?? quest.parent_id ?? '') === targetId) return true
    const sortedAnswerIds = Array.isArray(quest.sorted_answer_ids) ? quest.sorted_answer_ids : []
    if (sortedAnswerIds.some(id => String(id) === targetId)) return true
    const sortedAnswers = Array.isArray(quest.sorted_answers) ? quest.sorted_answers : []
    return sortedAnswers.some(answer => String(answer?.id ?? '') === targetId)
}

// An instant open lives or dies on the target's row descriptor existing by first commit.
// Start the quest fetch at click time — overlapping the panel insert/swipe — whenever the
// cache can't already produce that row (quest uncached, preview-scope, or a fresh cache
// that predates the target reply and would otherwise never refetch).
export function primeQuestForInstantOpen(questId, answerId) {
    if (!questId || !answerId) return
    if (!userHasFeature('instant_reply_nav')) return

    void prepareQuestForOpen(questId, answerId).catch(error => {
        console.warn('Failed to prime quest for instant open', { questId, answerId, error })
    })
}

// Awaitable readiness for targeted opens, including a preview-to-full upgrade already
// in flight. The cache having the target alone does not mean the thread is ready.
export async function prepareQuestForOpen(questId, answerId): Promise<Quest | null> {
    const cachedQuest = queryClient.getQueryData<Quest | null>(getQuestQueryKey(questId))
    if (
        cachedQuest &&
        cachedQuest.__knovAnswerHydrationScope !== 'stream-preview' &&
        cachedQuestContainsAnswer(cachedQuest, answerId) &&
        queryClient.getQueryState(getQuestQueryKey(questId))?.fetchStatus !== 'fetching'
    ) {
        return cachedQuest
    }

    const fetchFullQuest = () =>
        queryClient.fetchQuery({
            queryKey: getQuestQueryKey(questId),
            queryFn: async () => {
                const quest = await api.getQuestQuery(questId, {
                    compactThreadAnswers: true,
                    deferAnswerHydration: true,
                })
                if (quest) return quest

                const localQuest = queryClient.getQueryData<Quest | null>(getQuestQueryKey(questId))
                return localQuest || null
            },
            staleTime: 0,
            gcTime: QUEST_ENTITY_QUERY_POLICY.gcTime,
            networkMode: 'always',
        })
    const quest = await fetchFullQuest()
    // A simultaneous stream-preview fetch can be coalesced under this same key.
    // Once it finishes, issue the full request rather than treating that preview
    // as a completed open or revealing its still-changing membership.
    return quest?.__knovAnswerHydrationScope === 'stream-preview' ? fetchFullQuest() : quest
}

export function seedChildQuestFromAnswer(answer, childQuestId) {
    if (!answer?.id || !childQuestId) return null

    const existingQuest = queryClient.getQueryData<Quest | null>(getQuestQueryKey(childQuestId))
    const safeExistingQuest = existingQuest || ({} as Partial<Quest>)
    const cachedParent = getCachedAnswer(String(answer.id))
    const parent = cachedParent || safeExistingQuest.parent || answer
    const seededQuest = {
        ...safeExistingQuest,
        id: childQuestId,
        parent_id: answer.id,
        parent,
        sorted_answers: safeExistingQuest.sorted_answers || [],
        sorted_answer_ids: safeExistingQuest.sorted_answer_ids || [],
    } as Quest

    return cacheQuest(seededQuest, { skipParent: true })
}

export function refreshChildQuestInBackground(childQuestId) {
    if (!childQuestId) return

    void queryClient
        .fetchQuery({
            queryKey: getQuestQueryKey(childQuestId),
            queryFn: async () => {
                const quest = await api.getQuestQuery(childQuestId)
                if (quest) {
                    return cacheQuest(quest)
                }

                const localQuest = queryClient.getQueryData<Quest | null>(
                    getQuestQueryKey(childQuestId),
                )
                return localQuest || null
            },
            staleTime: 0,
            gcTime: QUEST_ENTITY_QUERY_POLICY.gcTime,
            networkMode: 'always',
        })
        .catch(error => {
            console.warn('Failed to refresh child quest after opening panel', {
                childQuestId,
                error,
            })
        })
}
